import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaCalendarAlt, FaMapMarkerAlt, FaSwimmer, FaTrophy, FaGlassCheers, FaClipboardList, FaFileDownload, FaMedal } from 'react-icons/fa';
import { upcomingEvents } from '../data/events';
import Seo from '../components/Seo';

function Events() {
  const { hash } = useLocation();

  // Scroll to a section when linked from elsewhere, e.g. /events#volunteers
  useEffect(() => {
    if (hash) {
      document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [hash]);

  const getEventMeta = (type) => {
    switch (type) {
      case 'gala': return { icon: FaSwimmer, color: 'text-primary-600', border: 'border-primary-600', bg: 'bg-primary-50' };
      case 'competition': return { icon: FaTrophy, color: 'text-blue-600', border: 'border-blue-600', bg: 'bg-blue-50' };
      case 'social': return { icon: FaGlassCheers, color: 'text-green-600', border: 'border-green-600', bg: 'bg-green-50' };
      case 'meeting': return { icon: FaClipboardList, color: 'text-purple-600', border: 'border-purple-600', bg: 'bg-purple-50' };
      default: return { icon: FaCalendarAlt, color: 'text-gray-600', border: 'border-gray-400', bg: 'bg-gray-50' };
    }
  };

  const featuredEvents = upcomingEvents.filter((event) => event.featured);

  return (
    <main className="py-16 bg-gradient-to-b from-gray-50 to-white">
      <Seo
        title="Events"
        description="Upcoming galas, competitions and social events at Marple Swimming Club."
        path="/events"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <h1 className="font-display text-5xl font-bold text-navy-900 mb-4">Club Events</h1>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            Galas, socials and meetings coming up at Marple Swimming Club.
          </p>
        </motion.div>

        {/* Featured Events */}
        {featuredEvents.length > 0 && (
          <motion.section
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <h2 className="font-display text-3xl font-bold text-navy-900 text-center mb-10">Featured Events</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredEvents.map((event, index) => {
                const meta = getEventMeta(event.type);
                const Icon = meta.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className={`bg-white p-6 rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 border-t-4 ${meta.border}`}
                  >
                    <div className={`${meta.color} mb-4`}>
                      <Icon className="text-3xl" />
                    </div>
                    <h3 className="text-xl font-bold text-navy-900 mb-2">{event.title}</h3>
                    <p className="flex items-center gap-2 font-semibold text-gray-800 mb-1">
                      <FaCalendarAlt className={meta.color} />
                      {event.date}
                    </p>
                    <p className="flex items-center gap-2 text-gray-600 mb-3">
                      <FaMapMarkerAlt className={meta.color} />
                      {event.location}
                    </p>
                    {event.description && (
                      <p className="text-gray-700 text-sm leading-relaxed">{event.description}</p>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </motion.section>
        )}

        {/* All Upcoming Events */}
        <motion.section
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h2 className="font-display text-3xl font-bold text-navy-900 text-center mb-10">All Upcoming Events</h2>
          {upcomingEvents.length === 0 ? (
            <p className="text-center text-gray-600 bg-white rounded-xl shadow-md p-8 max-w-xl mx-auto">
              No events currently scheduled &mdash; check back soon.
            </p>
          ) : (
            <div className="space-y-4 max-w-4xl mx-auto">
              {upcomingEvents.map((event, index) => {
                const meta = getEventMeta(event.type);
                const Icon = meta.icon;
                return (
                  <div
                    key={index}
                    className={`flex items-start gap-4 p-5 rounded-xl shadow-sm bg-white border-l-4 ${meta.border}`}
                  >
                    <div className={`${meta.color} ${meta.bg} p-3 rounded-full flex-shrink-0`}>
                      <Icon className="text-xl" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-navy-900 text-lg mb-1">{event.title}</h3>
                      <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-600">
                        <span className="flex items-center gap-1.5"><FaCalendarAlt />{event.date}</span>
                        <span className="flex items-center gap-1.5"><FaMapMarkerAlt />{event.location}</span>
                      </div>
                      {event.description && (
                        <p className="text-gray-700 text-sm mt-2">{event.description}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </motion.section>

        {/* Club News */}
        <motion.section
          id="club-news"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mb-16 max-w-4xl mx-auto scroll-mt-24"
        >
          <div className="bg-blue-50 border-2 border-blue-300 rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold text-blue-800 mb-4">The New Pool</h3>
            <p className="text-gray-800 mb-4 leading-relaxed">
              We are very excited to be moving training to the new pool at Marple Community Hub, hopefully
              before the end of the year. We have been in talks with Life Leisure and are negotiating session
              times for the club &mdash; we are hoping to keep sessions on Wednesday evenings and Sunday afternoons.
              We have a big waiting list of new members, but rest assured we will be prioritising our existing
              swimmers, making sure they are settled and happy at the new pool.
            </p>
            <p className="text-gray-800 mb-4 leading-relaxed">
              We are also hoping to introduce new <strong>Masters</strong> (coached sessions for adult swimmers) and
              <strong> Adults</strong> (non-coached, more casual leisurely swims) sessions.{' '}
              <Link to="/masters-interest" className="text-primary-600 font-semibold underline hover:text-primary-700">
                Register your interest
              </Link>.
            </p>
            <p className="text-gray-800 leading-relaxed">
              <strong>Membership fees:</strong> due to pool hire and other costs increasing, membership fees for all
              swimmers will increase to <strong>&pound;30 per month from 1st January 2027</strong>. This will be
              adjusted automatically by GoCardless.
            </p>
          </div>
        </motion.section>

        {/* Volunteers Needed */}
        <motion.section
          id="volunteers"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mb-16 max-w-4xl mx-auto scroll-mt-24"
        >
          <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold text-amber-800 mb-4">Volunteers Needed!</h3>
            <p className="text-gray-800 mb-4 leading-relaxed">
              Marple Swimming Club is mainly run by a team of volunteers who organise fundraising,
              safeguarding, Swim England accreditation, social events, galas and much more. As we prepare
              to move to the new pool and expand club membership, it is more important than ever that we
              have a strong team of volunteers. We are currently looking for:
            </p>
            <ul className="space-y-3 mb-4">
              <li className="text-gray-800">
                <strong>Team Managers</strong> &mdash; the volunteers you see in club t-shirts at galas and other events,
                making sure swimmers are in the right place at the right time. The role requires an online
                safeguarding course, a Swim England team manager course and a DBS check.{' '}
                <a
                  href="https://www.swimming.org/careers/team-manager-role/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 font-semibold underline hover:text-primary-700"
                >
                  More about the role
                </a>
              </li>
              <li className="text-gray-800">
                <strong>Tadpoles/Mavis Wallis Coordinator</strong> &mdash; act as a liaison between Stockport Swimming
                Association and the club, and co-ordinate the annual Tadpoles and Mavis Wallis galas.
              </li>
            </ul>
            <p className="text-gray-800 mb-4">
              If you think you could do any of these roles, please speak to Lucy or Dan for more information!
            </p>
            <p className="text-gray-800">
              <strong>Next Committee Meeting:</strong> Thursday 8th October 2026, The Navigation, Marple, 8pm &mdash; all welcome!
            </p>
          </div>
        </motion.section>

        {/* Fundraising */}
        <motion.section
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mb-16 max-w-4xl mx-auto"
        >
          <div className="bg-purple-50 border-2 border-purple-300 rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold text-purple-800 mb-4">Fundraising</h3>
            <p className="text-gray-800 mb-4 leading-relaxed">
              When we move to the new pool we will no longer be able to sell cakes, which is an important
              income for club funds. We are always needing raffle prizes for home galas and presentation
              evening, so any donations are gratefully received. We are also looking into other events to
              raise funds, such as a quiz night or a fun inflatable session.
            </p>
            <p className="text-gray-800">
              If you have any ideas for fundraising or would like to be involved, please message Jane
              on{' '}
              <a href="mailto:Marplescfundraising@gmail.com" className="text-primary-600 font-semibold underline hover:text-primary-700">
                Marplescfundraising@gmail.com
              </a>
            </p>
          </div>
        </motion.section>

        {/* Intertown Galas */}
        <motion.section
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mb-16 max-w-4xl mx-auto"
        >
          <div className="bg-green-50 border-2 border-green-500 rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold text-green-800 mb-6">What Are Intertown Galas?</h3>
            <div className="space-y-5">
              <div>
                <h4 className="font-bold text-green-800 mb-1">The league</h4>
                <p className="text-gray-800">Marple Swimming Club are members of the Intertown Friendly League (ITFL), a league of swimming clubs from around Stockport. The teams are Ashton, Wilmslow, Hyde, Saracens (Hazel Grove), Romiley, Saddleworth and Dukinfield.</p>
              </div>
              <div>
                <h4 className="font-bold text-green-800 mb-1">Who swims?</h4>
                <p className="text-gray-800">Swimmers in school years 5/6 and 7/8 swim in the juniors and compete over 25m. Seniors are from year 9/10 and Y11 and older and compete over 50m. Races are split into boys and girls and include individual backstroke, breaststroke, butterfly and freestyle races, plus freestyle and medley relays (where each swimmer swims a different stroke).</p>
              </div>
              <div>
                <h4 className="font-bold text-green-800 mb-1">How do we enter?</h4>
                <p className="text-gray-800">If your child is eligible you will be sent an invitation by email and will need to respond on Swim Manager. It takes a lot of time for the organisers to put swimmers in the correct races, so please respond as soon as you can and let us know as far in advance as possible if your child can't attend.</p>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Recent Achievements */}
        <motion.section
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mb-16 max-w-4xl mx-auto"
        >
          <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-8">
            <div className="flex items-center gap-3 mb-4">
              <FaMedal className="text-3xl text-amber-600" />
              <h3 className="font-display text-2xl font-bold text-amber-800">Recent Achievements</h3>
            </div>
            <p className="text-gray-800 mb-3 leading-relaxed">
              <strong>Congratulations to Imogen, Beth, Abby, Emily and Harry</strong> who have all achieved SEQ qualifications in 2025 and we look forward to seeing more Level 1 and 2 achievements in 2026!
            </p>
            <p className="text-gray-800 leading-relaxed">
              These swimmers have completed their SEQ Level 1 Swimming Assistant (Poolside) qualification to allow them to assist with swimming lessons, and SEQ Level 2 Teaching Swimming (Poolside) qualifications to allow them to plan, prepare, deliver and evaluate swimming lessons with support from Lucy and Dan.
            </p>
          </div>
        </motion.section>

        {/* Newsletter */}
        <motion.section
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center bg-white rounded-2xl shadow-md p-10 max-w-2xl mx-auto"
        >
          <h3 className="font-display text-2xl font-bold text-navy-900 mb-3">Want More Details?</h3>
          <p className="text-gray-700 mb-6">
            View our full newsletter for complete information about upcoming events,
            volunteer opportunities, and club updates.
          </p>
          <a
            href="/newsletter.jpg"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-primary-600 text-white px-6 py-3 rounded-full font-bold hover:bg-primary-700 transition-all duration-300 shadow-md"
          >
            <FaFileDownload />
            View Newsletter
          </a>
        </motion.section>
      </div>
    </main>
  );
}

export default Events;
